<script lang="ts" setup>
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import TableFilter from '@/components/table/TableFilter.vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import PageHeader from '@/components/global/page-header/PageHeader.vue'
import { computed, onMounted, ref } from 'vue'
import { Icon } from '@iconify/vue'
import { RouterLink, useRouter } from 'vue-router'
import { products } from '@/constants/productSpecification'
import StatusTag from '@/components/table/StatusTag.vue'
import { getProductList } from '@/api/products'
import {FabricStatusEnum, type Product} from "@/types/product-types";

const searchQuery = ref('')
const router = useRouter()
const loading = ref<boolean>(true)
const productsList = ref<Product[]>([])
const statusFilters = ref<string[]>([])

const productFilterOptions = [
  { value: 'all', label: 'All', id: 'fabric-status-all' },
  { value: 'finished', label: 'Finished', id: 'fabric-status-finished' },
  { value: 'unfinished', label: 'Unfinished', id: 'fabric-status-unfinished' }
]

const filteredProducts = computed(() => {
  const query = searchQuery.value.toLowerCase()
  let filtered = productsList.value.filter(
      (product: Product) =>
          product.finishedReference?.toLowerCase().includes(query) ||
          (product.displayedReference && product.displayedReference.toLowerCase().includes(query)) ||
          (product.fabricStatus.toString().toLowerCase().includes(query))
  )

  if (statusFilters.value.length > 0 && !statusFilters.value.includes('all')) {
    filtered = filtered.filter((product: Product) => {
      const fabricStatus = product.fabricStatus === FabricStatusEnum.Finished ? 'finished' : 'unfinished'
      return statusFilters.value.includes(fabricStatus)
    })
  }

  return filtered
})

const handleFilterChange = (filters: string[]) => {
  statusFilters.value = filters
}

onMounted(async () => {
  productsList.value = await getProductList()
  loading.value = false
})
</script>

<template>
  <PageHeader
      title="Product Specification"
      :description="`Total: ${productsList.length} | Filtered: ${filteredProducts.length}`"
      buttonText="Add New Product Specification"
      buttonIcon="dashicons:products"
      :buttonClick="() => router.push('/productSpecification/add')"
  />
  <div class="flex justify-end py-2">
    <RouterLink
      to="/productSpecification/archived"
      class="flex items-center gap-x-2 group hover:text-primary-default"
    >
      <span>Archived Product Specifications</span>
      <Icon
        icon="material-symbols:arrow-forward"
        class="text-2xl group-hover:translate-x-2 transition-transform text-primary-500"
      />
    </RouterLink>
  </div>
  <DataTable
      :value="filteredProducts"
      paginator
      :rows="10"
      tableStyle="min-width: 50rem"
      stripedRows
      paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
      :rowsPerPageOptions="[10, 25]"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} products"
      :loading="loading"
      :pt="{
      table: loading && 'min-h-[30rem]'
    }"
      :pt-options="{
      mergeProps: true
    }"
  >
    <template #empty> <p class="text-center">No product found.</p> </template>

    <template #header>
      <div class="flex items-center justify-between font-normal">
        <TableSearchInput v-model:searchQuery="searchQuery" />

        <TableFilter
            :filter-options="productFilterOptions"
            filter-title="Filter Fabric Status"
            @filter-change="handleFilterChange"
        />
      </div>
    </template>
    <Column field="finishedReference" header="Product Reference" :sortable="true">
      <template #body="slotProps">
        <RouterLink
            :to="`/productSpecification/${slotProps.data.id}`"
            class="font-medium text-neutral-800 block hover:underline"
            v-if="slotProps.data.fabricStatus === FabricStatusEnum.Finished"
        >
          {{ slotProps.data.finishedReference }}
        </RouterLink>
        <RouterLink
            :to="`/productSpecification/${slotProps.data.id}`"
            class="font-medium text-neutral-800 block hover:underline"
            v-else-if="slotProps.data.fabricStatus === FabricStatusEnum.Unfinished"
        >
          {{ slotProps.data.unfinishedReference }}
        </RouterLink>
      </template>
    </Column>

    <Column field="construction" header="Construction" :sortable="true">
      <template #body="slotProps">
        {{ slotProps.data.construction }}
      </template>
    </Column>

    <Column field="fabricStatus" header="Status" :sortable="true">
      <template #body="slotProps">
        <StatusTag :status="slotProps.data.fabricStatus === FabricStatusEnum.Finished ? 'Finished' : 'Unfinished'" />
      </template>
    </Column>

    <Column field="action">
      <template #body="slotProps">
        <RouterLink
            :to="`/productSpecification/${slotProps.data.id}`"
            class="flex items-center gap-x-2 group hover:text-primary-default"
        >
          <span> More Details</span>
          <Icon
              icon="material-symbols:more-up"
              class="text-2xl group-hover:translate-x-2 transition-transform text-primary-500"
          />
        </RouterLink>
      </template>
    </Column>
    <!------------------------------------------ pagination---------------------------------------------------->
    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl" />
      </Button>
    </template>
  </DataTable>
</template>
